/**
 * Performance optimization utilities for Vue components
 * These utilities provide memoization, debouncing, and throttling capabilities
 * without breaking existing component functionality.
 */

/**
 * Advanced debounce function with immediate execution option and cleanup
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds
 * @param {Object} options - Configuration options
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait, options = {}) => {
  const { leading = false, trailing = true, maxWait = null } = options;

  let timeoutId;
  let lastCallTime;
  let lastInvokeTime = 0;
  let lastArgs;
  let lastThis;
  let result;

  function invokeFunc(time) {
    const args = lastArgs;
    const thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    lastInvokeTime = time;
    timeoutId = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;

    return maxWait === null
      ? timeWaiting
      : Math.min(timeWaiting, maxWait - timeSinceLastInvoke);
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    return (
      lastCallTime === undefined ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (maxWait !== null && timeSinceLastInvoke >= maxWait)
    );
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timeoutId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timeoutId = undefined;

    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timeoutId = undefined;
  }

  function flush() {
    return timeoutId === undefined ? result : trailingEdge(Date.now());
  }

  function pending() {
    return timeoutId !== undefined;
  }

  function debounced(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timeoutId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxWait) {
        timeoutId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timeoutId === undefined) {
      timeoutId = setTimeout(timerExpired, wait);
    }
    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  debounced.pending = pending;
  return debounced;
};

/**
 * Throttle function for limiting function execution frequency
 * @param {Function} func - Function to throttle
 * @param {number} wait - Delay in milliseconds
 * @param {Object} options - Configuration options
 * @returns {Function} Throttled function
 */
export const throttle = (func, wait, options = {}) => {
  const { leading = true, trailing = true } = options;

  return debounce(func, wait, {
    leading,
    trailing,
    maxWait: wait,
  });
};

/**
 * Memoization function with cache size limit and TTL
 * @param {Function} fn - Function to memoize
 * @param {Object} options - Configuration options
 * @returns {Function} Memoized function
 */
export const memoize = (fn, options = {}) => {
  const {
    maxSize = 100,
    ttl = null, // Time to live in milliseconds
    keyGenerator = (...args) => JSON.stringify(args),
  } = options;

  const cache = new Map();
  const timestamps = new Map();

  return function memoized(...args) {
    const key = keyGenerator(...args);
    const now = Date.now();

    // Check if cache entry exists and is still valid
    if (cache.has(key)) {
      if (ttl === null || now - timestamps.get(key) < ttl) {
        return cache.get(key);
      } else {
        // Remove expired entry
        cache.delete(key);
        timestamps.delete(key);
      }
    }

    // Check cache size limit
    if (cache.size >= maxSize) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
      timestamps.delete(firstKey);
    }

    // Compute and cache result
    const result = fn.apply(this, args);
    cache.set(key, result);
    timestamps.set(key, now);

    return result;
  };
};

/**
 * Request deduplication utility
 * Prevents multiple identical requests from being made simultaneously
 */
export class RequestDeduplicator {
  constructor() {
    this.pendingRequests = new Map();
  }

  async deduplicate(key, requestFn) {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  cancel(key) {
    if (this.pendingRequests.has(key)) {
      this.pendingRequests.delete(key);
    }
  }

  clear() {
    this.pendingRequests.clear();
  }
}

/**
 * Performance monitoring utility
 * Helps track component performance metrics
 */
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
  }

  startTiming(name) {
    this.metrics.set(name, {
      start: performance.now(),
      end: null,
      duration: null,
    });
  }

  endTiming(name) {
    const metric = this.metrics.get(name);
    if (metric) {
      metric.end = performance.now();
      metric.duration = metric.end - metric.start;
    }
    return metric;
  }

  getMetrics() {
    return Object.fromEntries(this.metrics);
  }

  clear() {
    this.metrics.clear();
  }
}

/**
 * Simple debounce for Vue components (backwards compatible)
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const simpleDebounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Simple throttle for Vue components (backwards compatible)
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const simpleThrottle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};
